import React, { useState, useEffect } from "react";
import {
  RefreshCw,
  Trash2,
  AlertCircle,
  CheckCircle,
  Wifi,
  WifiOff,
} from "lucide-react";
import {
  forceServiceWorkerUpdate,
  clearAllCaches,
  checkForServiceWorkerUpdate,
  activateWaitingServiceWorker,
  getServiceWorkerStatus,
} from "../../utils/serviceWorkerUtils";

const ServiceWorkerSettings: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [swStatus, setSwStatus] = useState({
    registered: false,
    active: false,
    waiting: false,
    installing: false,
  });
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error" | "info";
    text: string;
  } | null>(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Check service worker status on mount
  useEffect(() => {
    loadServiceWorkerStatus();
  }, []);

  const loadServiceWorkerStatus = async () => {
    const status = await getServiceWorkerStatus();
    setSwStatus(status);

    const hasUpdate = await checkForServiceWorkerUpdate();
    setUpdateAvailable(hasUpdate);
  };

  const handleClearCaches = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const success = await clearAllCaches();
      if (success) {
        setMessage({
          type: "success",
          text: "All caches cleared successfully! Refresh the page to see changes.",
        });
      } else {
        setMessage({
          type: "error",
          text: "Failed to clear caches. Please try again.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "An error occurred while clearing caches.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceUpdate = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      setMessage({
        type: "info",
        text: "Updating service worker... The page will reload automatically.",
      });

      // This will reload the page
      await forceServiceWorkerUpdate();
    } catch (error) {
      setMessage({
        type: "error",
        text: "An error occurred while updating the service worker.",
      });
      setIsLoading(false);
    }
  };

  const handleActivateUpdate = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const success = await activateWaitingServiceWorker();
      if (success) {
        setMessage({
          type: "info",
          text: "Activating update... The page will reload.",
        });
      } else {
        setMessage({
          type: "error",
          text: "No update available to activate.",
        });
        setIsLoading(false);
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "An error occurred while activating the update.",
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          App Cache & Updates
        </h2>
        <p className="text-slate-600">
          Manage cached data and service worker updates
        </p>
      </div>

      {/* Online Status */}
      <div className="flex items-center space-x-2 p-4 rounded-lg bg-slate-50 border border-slate-200">
        {isOnline ? (
          <>
            <Wifi className="w-5 h-5 text-green-600" />
            <span className="text-slate-900 font-medium">Online</span>
          </>
        ) : (
          <>
            <WifiOff className="w-5 h-5 text-red-600" />
            <span className="text-slate-900 font-medium">Offline</span>
          </>
        )}
      </div>

      {/* Service Worker Status */}
      <div className="space-y-2">
        <h3 className="font-semibold text-slate-900">Service Worker Status</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center space-x-2">
            <div
              className={`w-3 h-3 rounded-full ${
                swStatus.registered ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm text-slate-600">Registered</span>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className={`w-3 h-3 rounded-full ${
                swStatus.active ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm text-slate-600">Active</span>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className={`w-3 h-3 rounded-full ${
                swStatus.installing ? "bg-yellow-500" : "bg-slate-300"
              }`}
            />
            <span className="text-sm text-slate-600">Installing</span>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className={`w-3 h-3 rounded-full ${
                swStatus.waiting ? "bg-blue-500" : "bg-slate-300"
              }`}
            />
            <span className="text-sm text-slate-600">Update Waiting</span>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div
          className={`p-4 rounded-lg flex items-start space-x-3 ${
            message.type === "success"
              ? "bg-green-50 border border-green-200"
              : message.type === "error"
              ? "bg-red-50 border border-red-200"
              : "bg-blue-50 border border-blue-200"
          }`}
        >
          {message.type === "success" ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle
              className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                message.type === "error" ? "text-red-600" : "text-blue-600"
              }`}
            />
          )}
          <p
            className={`text-sm ${
              message.type === "success"
                ? "text-green-800"
                : message.type === "error"
                ? "text-red-800"
                : "text-blue-800"
            }`}
          >
            {message.text}
          </p>
        </div>
      )}

      {/* Update Available Alert */}
      {updateAvailable && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-blue-800 mb-2">
                A new version of the app is available!
              </p>
              <button
                onClick={handleActivateUpdate}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
              >
                Update Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-900">Troubleshooting</h3>

        {/* Clear Caches */}
        <button
          onClick={handleClearCaches}
          disabled={isLoading}
          className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Trash2 className="w-5 h-5" />
          <span>Clear All Caches</span>
        </button>

        {/* Force Update */}
        <button
          onClick={handleForceUpdate}
          disabled={isLoading}
          className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`} />
          <span>Force Complete Update</span>
        </button>

        {/* Explanation */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-xs text-slate-600">
            <strong>Clear All Caches:</strong> Removes all cached data. Use this
            if you're seeing old content.
            <br />
            <br />
            <strong>Force Complete Update:</strong> Unregisters the service
            worker and reloads the app. Use this if the app isn't working
            properly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServiceWorkerSettings;
